import { useForm, useWatch } from "react-hook-form";
import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import FormInputComponent from "@via/components/inputs/form-input-component";
import { SelectOption } from "@via/utils/interfaces";
import { type JSX, useEffect } from "react";
import { PipelineFilter } from "@via/pages/sample-trackers/samples/types/filters.ts";
import {
  getRunStatusLabel,
  getRunStatusText,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import { RunStatus } from "common";

interface AddPipelineFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddFilter: (filter: PipelineFilter) => void;
  editingFilter?: PipelineFilter;
}

interface FilterFormData {
  field: PipelineFilter["field"];
  operator: PipelineFilter["operator"];
  value: string | number;
}

// Pipeline field options
const pipelineFieldOptions: SelectOption[] = [
  { label: "Pipeline ID", value: "pipelineId" },
  { label: "Pipeline Name", value: "pipelineName" },
  { label: "Pipeline Version", value: "pipelineVersion" },
  { label: "Run Name", value: "runName" },
  { label: "Run Status", value: "status" },
];

// Operator options for pipeline filters
const operatorOptions: SelectOption[] = [
  { label: "equals", value: "eq" },
  { label: "contains", value: "like" },
];

const statusOptions: SelectOption[] = (() => {
  // Group statuses by Runs-table label to avoid duplicates in dropdown.
  const groups = new Map<string, RunStatus[]>();
  for (const s of Object.values(RunStatus)) {
    const t = getRunStatusText(s);
    const label = typeof t === "string" && t.trim() !== "" ? t : String(s);
    const arr = groups.get(label) ?? [];
    arr.push(s);
    groups.set(label, arr);
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, statuses]) => {
      // Store a pipe-separated list so backend can treat it as an IN filter.
      const value = statuses.join("|");
      return {
        label,
        value,
        metadata: { badgeStatus: statuses[0] },
      } as SelectOption;
    });
})();

const formatStatusOptionLabel = (
  option: SelectOption,
  { context }: { context: "menu" | "value" }
): JSX.Element | string => {
  const badgeStatus = String(option?.metadata?.badgeStatus ?? "");
  if (!badgeStatus) return option.label ?? "";
  // Match Runs table: show the same badge. Keep text only for the selected "value" pill if needed.
  if (context === "value") return getRunStatusLabel(badgeStatus);
  return (
    <div className="flex items-center gap-2">
      {getRunStatusLabel(badgeStatus)}
    </div>
  );
};

export default function AddPipelineFilterModal({
  isOpen,
  onClose,
  onAddFilter,
  editingFilter,
}: AddPipelineFilterModalProps): JSX.Element {
  const isEditMode = !!editingFilter;

  const { control, handleSubmit, reset, watch } = useForm<FilterFormData>({
    defaultValues: {
      field: editingFilter?.field || "pipelineName",
      operator: editingFilter?.operator || "like",
      value: editingFilter?.value || "",
    },
  });

  // Reset form when editingFilter changes or modal opens/closes
  useEffect(() => {
    if (isOpen) {
      const nextField = editingFilter?.field || "pipelineName";
      reset({
        field: nextField,
        operator:
          nextField === "status" ? "eq" : editingFilter?.operator || "like",
        value: editingFilter?.value || "",
      });
    }
  }, [isOpen, editingFilter, reset]);

  const selectedField = useWatch({
    control,
    name: "field",
  });

  useEffect(() => {
    if (selectedField === "status") {
      // status filter only supports equals
      reset(curr => ({
        ...curr,
        operator: "eq" as PipelineFilter["operator"],
      }));
    }
  }, [selectedField, reset]);

  const onSubmit = (data: FilterFormData): void => {
    if (
      data.field &&
      data.operator &&
      data.value !== undefined &&
      data.value !== null &&
      String(data.value).trim() !== ""
    ) {
      onAddFilter({
        field: data.field,
        operator: data.operator,
        value: data.value,
      });
      reset();
      onClose();
    }
  };

  const isFormValid = (): boolean => {
    const formValues = watch();
    return !!(
      formValues.field &&
      formValues.operator &&
      formValues.value !== undefined &&
      formValues.value !== null &&
      String(formValues.value).trim() !== ""
    );
  };

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title={isEditMode ? "Edit Run Filter" : "Add Run Filter"}
      showButtons={false}
      size="small">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Field Selection */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-900">
            Select field
          </label>
          <SelectComponent
            control={control}
            name="field"
            options={pipelineFieldOptions}
            placeholder="Select field"
            enableClear={false}
          />
        </div>

        {/* Operator Selection */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-900">
            Operator
          </label>
          <SelectComponent
            control={control}
            name="operator"
            options={operatorOptions}
            placeholder="contains"
            enableClear={false}
            readOnly={selectedField === "status"}
          />
        </div>

        {/* Value Input */}
        <div>
          {selectedField === "status" ? (
            <>
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Value
              </label>
              <SelectComponent
                control={control}
                name="value"
                options={statusOptions}
                placeholder="Select status"
                enableClear={false}
                formatOptionLabel={formatStatusOptionLabel}
              />
            </>
          ) : (
            <FormInputComponent
              control={control}
              name="value"
              label="Value"
              placeholder="Enter value"
              type="text"
              disabled={!selectedField}
              validations={{
                required: "Value is required",
              }}
            />
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid()}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
            {isEditMode ? "Update Filter" : "Add Filter"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
